import React, { forwardRef } from "react";
import { FieldError } from "react-hook-form";
import { Checkbox, Typography } from "@kaltura/ds-react-components";
import SmartContainer from "./smart-container/SmartContainer";
import { uniqueId } from "lodash";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { FormFieldError } from "@kaltura/mediaspace-shared-ui/form";
import styled from "@emotion/styled";
import { useUtilityClasses } from "./fieldClasses";
import clsx from "clsx";

export interface Props {
    title: string;
    name: string;
    id?: string;
    error?: FieldError;
    onChange?: (checked: boolean) => void;
    /** initial checkbox state */
    checked?: boolean;
    disabled?: boolean;
    mandatoryText?: string;
    value?: string;
    required?: boolean;
    className?: string;
    dataTestId?: string;
}

const Label = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(0, 1),
}));

const CheckboxField = forwardRef<HTMLInputElement, Props>((props, buttonRef) => {
    const {
        name,
        id = uniqueId("input_"),
        title,
        checked,
        disabled,
        onChange,
        mandatoryText,
        error,
        value,
        required = false,
        className,
        dataTestId,
    } = props;

    const classes = useUtilityClasses();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event.target.checked);
        }
    };

    const CheckBoxStyledContainer = styled(SmartContainer)(({ theme }) => ({
        maxWidth: "600px",
        gridTemplateColumns: "100%",
        [theme.breakpoints.down(theme.breakpoints.values.md)]: {
            gridTemplateColumns: "100%",
        },
    }));

    const StyledDivContainer = styled("div")({
        display: "inline-flex",
    });

    const StyledLabelContainer = styled("label")(({ theme }) => ({
        display: "inline-flex",
        color: theme.kaltura.palette.tone2,
    }));

    return (
        <CheckBoxStyledContainer className={clsx(classes.root, className)}>
            <span className={classes.input}>
                <StyledDivContainer>
                    <Checkbox
                        name={name}
                        id={id}
                        onChange={handleChange}
                        defaultChecked={checked}
                        disabled={disabled}
                        inputRef={buttonRef}
                        value={value}
                        required={required}
                        data-testid={dataTestId}
                    />
                    <StyledLabelContainer htmlFor={id} className={classes.label}>
                        <Label variant={"body2"} component={"span"}>
                            {HtmlReactParser(title)}
                            {required ? " *" : ""}
                        </Label>
                    </StyledLabelContainer>
                </StyledDivContainer>
                <div aria-live="polite">{error && <FormFieldError>{mandatoryText}</FormFieldError>}</div>
            </span>
        </CheckBoxStyledContainer>
    );
});

export default CheckboxField;
